import { Check, X } from "lucide-react";
import { withUtm } from "@/components/axa/utm";

const COL_HEADERS = [
  { label: "Portage de factures", muted: false },
  { label: "Service Standard", muted: true },
  { label: "Service Clé en Main", muted: true },
];

type Cell = boolean | "fees";

interface Row {
  label: string;
  cells: [Cell, Cell, Cell];
}

interface Group {
  title: string;
  rows: Row[];
}

const groups: Group[] = [
  {
    title: "Admin",
    rows: [
      { label: "Frais", cells: ["fees", "fees", "fees"] },
      { label: "Avance d'acompte", cells: [true, true, true] },
      { label: "Référencement de nouveaux lieux et prestataires via Naboo", cells: [true, true, true] },
    ],
  },
  {
    title: "Recherche de lieu",
    rows: [
      { label: "Autonomie complète sur la plateforme", cells: [true, true, true] },
      { label: "Délégation du brief à un expert événementiel avec suggestions et support illimités", cells: [false, true, true] },
      { label: "Assistance illimitée pour négocier les prix et conditions", cells: [false, true, true] },
    ],
  },
  {
    title: "Gestion d'événement",
    rows: [
      { label: "Application événementielle pour organisateurs et participants", cells: [false, true, true] },
      { label: "Visite du site", cells: [false, false, true] },
      { label: "Coordination des prestataires après signature du devis", cells: [false, false, true] },
      { label: "Gestionnaire d'événement sur place pendant l'événement (>40K€)", cells: [false, false, true] },
    ],
  },
];

const CellRender = ({ value }: { value: Cell }) => {
  if (value === "fees") {
    return (
      <a
        href={withUtm("https://axa-go.naboo.app/")}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center justify-center group cursor-pointer"
      >
        <span className="font-medium text-[#111111] group-hover:underline text-xs md:text-sm">
          Découvrez nos frais
        </span>
        <span className="mt-1 text-sm flex items-center justify-center group-hover:opacity-80 transition-opacity">
          <span className="filter blur-[3px] text-gray-400">€€</span>
          <span className="ml-1 font-medium text-[#111111]">%</span>
        </span>
      </a>
    );
  }
  return (
    <div className="flex items-center justify-center">
      <div
        className={`rounded-full w-6 h-6 flex items-center justify-center ${
          value ? "bg-[#C6E278]" : "bg-gray-200"
        }`}
      >
        {value ? (
          <Check size={16} className="text-[#111111]" strokeWidth={3} />
        ) : (
          <X size={16} className="text-white" strokeWidth={3} />
        )}
      </div>
    </div>
  );
};

const AxaPreferredTerms = () => {
  return (
    <section className="bg-[#f5f7f0] py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1126px] mx-auto">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-[#111111] text-center mb-6">
          Bénéficiez de conditions préférentielles
        </h2>

        <div className="overflow-x-auto pb-4">
          <table className="w-full border-collapse min-w-[800px] rounded-[10px] overflow-hidden bg-white">
            <thead>
              <tr>
                <th className="w-[40px] h-[80px] border border-gray-200 bg-white"></th>
                <th className="w-[280px] h-[80px] border border-gray-200 bg-white"></th>
                {COL_HEADERS.map((h) => (
                  <th
                    key={h.label}
                    className={`w-[200px] h-[80px] border border-gray-200 ${
                      h.muted ? "bg-[#F3F4F5]" : "bg-white"
                    } text-center p-3 font-bold text-[#111111] text-sm md:text-base`}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) =>
                group.rows.map((row, rIdx) => (
                  <tr key={`${group.title}-${rIdx}`}>
                    {rIdx === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="border border-gray-200 bg-white p-0 align-middle"
                      >
                        <div className="flex h-full items-center justify-center">
                          <span className="rotate-[270deg] whitespace-nowrap text-[#111111] font-medium text-sm">
                            {group.title}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="border border-gray-200 p-3 md:p-4 text-left text-xs md:text-sm text-[#111111]">
                      {row.label}
                    </td>
                    {row.cells.map((cell, i) => (
                      <td
                        key={i}
                        className={`border border-gray-200 p-3 ${
                          COL_HEADERS[i].muted ? "bg-[#F3F4F5]" : "bg-white"
                        }`}
                      >
                        <CellRender value={cell} />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="text-center mt-8">
          <a
            href={withUtm("https://axa-go.naboo.app/")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#C6E278] text-[#111111] text-sm font-semibold px-6 py-3 hover:scale-105 transition-transform"
          >
            Découvrir la plateforme AXA
          </a>
        </div>
      </div>
    </section>
  );
};

export default AxaPreferredTerms;
